import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ArrowRight, Heart, Users, Package, Shield, RefreshCw, Flame, DollarSign, Gift
} from 'lucide-react';
import axios from 'axios';
import { TransparencyNotice, TransparencyCheckbox } from './components/TransparencyNotice';
import { DonateFAQ } from './components/DonateFAQ';
import { FundAllocationChart } from './components/FundAllocationChart';
import { DonorWallSection } from './components/DonorWallSection';
import { MerchTransparencyNotice } from './components/MerchTransparencyNotice';
import { isValidEmail, getEmailError } from '../utils/emailValidation';

const logger = { error: (...a) => console.error(...a) };
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Donate = () => {
  const [selectedAmount, setSelectedAmount] = useState({ amount: 100, id: 'tier_100' });
  const [customAmount, setCustomAmount] = useState('');
  const [honorName, setHonorName] = useState('');
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [transparencyAcknowledged, setTransparencyAcknowledged] = useState(false);
  const [fundraisingGoal, setFundraisingGoal] = useState(null);
  const [donorWall, setDonorWall] = useState([]);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringInterval, setRecurringInterval] = useState('month');
  const [wallDisplay, setWallDisplay] = useState('anonymous');
  const [allocation, setAllocation] = useState(null);
  const [fundDesignation, setFundDesignation] = useState({ enabled: false, intro_copy: '', options: [] });
  const [selectedFundId, setSelectedFundId] = useState('');
  const [donationTiers, setDonationTiers] = useState(null);
  const [dedicationKit, setDedicationKit] = useState('');
  const [campaignSlug, setCampaignSlug] = useState('');
  const [campaign, setCampaign] = useState(null);
  // Hard-block state: donor came from a campaign link that's archived,
  // inactive, deleted, or does not exist. Spec requires we do NOT
  // silently convert this into a general fund donation — the donor
  // must explicitly opt in to general or view active campaigns.
  const [campaignBlocked, setCampaignBlocked] = useState(null); // { slug }
  const location = useLocation();

  const fetchAllocation = useCallback(async () => {
    try { const res = await axios.get(`${BACKEND_URL}/api/public/fund-allocation`); setAllocation(res.data); }
    catch (e) { logger.error('Error loading fund allocation:', e); }
  }, []);

  const fetchFundraisingGoal = useCallback(async () => {
    try { const r = await axios.get(`${BACKEND_URL}/api/public/fundraising-goal`); setFundraisingGoal(r.data); }
    catch (e) { logger.error('Error fetching goal:', e); }
  }, []);

  const fetchDonorWall = useCallback(async () => {
    try { const r = await axios.get(`${BACKEND_URL}/api/public/donor-wall`); setDonorWall(r.data); }
    catch (e) { logger.error('Error fetching donor wall:', e); }
  }, []);

  const fetchDonationTiers = useCallback(async () => {
    try { const res = await axios.get(`${BACKEND_URL}/api/public/site-content/donation_tiers`); if (res.data?.data?.tiers) setDonationTiers(res.data.data); }
    catch {}
  }, []);

  const fetchFundDesignation = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/site/donate-fund-designation`);
      setFundDesignation(r.data || { enabled: false, options: [] });
    } catch (e) { logger.error('Error fetching fund designation:', e); }
  }, []);

  useEffect(() => { fetchFundraisingGoal(); fetchDonorWall(); fetchAllocation(); fetchDonationTiers(); fetchFundDesignation(); },
    [fetchFundraisingGoal, fetchDonorWall, fetchAllocation, fetchDonationTiers, fetchFundDesignation]);

  // Default tiers (overridden by admin if configured)
  const tiers = donationTiers?.tiers || [
    { id: 'tier_50', amount: 50, label: 'Supports a Family for 2 Weeks', popular: false, enabled: true },
    { id: 'tier_100', amount: 100, label: 'Supports 1 Family', popular: true, enabled: true },
    { id: 'tier_200', amount: 200, label: 'Supports 2 Families', popular: false, enabled: true },
    { id: 'tier_500', amount: 500, label: 'Supports 5 Families', popular: false, enabled: true },
    { id: 'tier_1000', amount: 1000, label: 'Supports 10 Families', popular: false, enabled: true },
  ];
  const activeTiers = tiers.filter(t => t.enabled !== false);

  // Prefill amount + kit dedication + campaign from URL params
  // (e.g. /donate?amount=25&kit=Men%27s%20Hygiene%20Kit or
  //  /donate?campaign=back-to-school-2026).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const amt = parseFloat(params.get('amount'));
    const kit = params.get('kit');
    const cslug = params.get('campaign');
    if (kit) setDedicationKit(kit);
    if (cslug) {
      setCampaignSlug(cslug);
      setCampaignBlocked(null);
      // Fetch the campaign for the banner (name + goal etc).
      (async () => {
        try {
          const r = await axios.get(`${BACKEND_URL}/api/public/campaigns/${cslug}`);
          setCampaign(r.data);
        } catch {
          // Campaign is archived / inactive / deleted / nonexistent.
          // HARD BLOCK: do NOT silently fall through to general fund.
          // Show a screen with explicit opt-in choices.
          setCampaignSlug('');
          setCampaign(null);
          setCampaignBlocked({ slug: cslug });
        }
      })();
    }
    if (!Number.isNaN(amt) && amt > 0 && activeTiers.length > 0) {
      const exact = activeTiers.find(t => Number(t.amount) === amt);
      if (exact) {
        setSelectedAmount({ amount: exact.amount, id: exact.id });
        setCustomAmount('');
      } else {
        setSelectedAmount(null);
        setCustomAmount(String(amt));
      }
      // Scroll to donate form
      setTimeout(() => {
        const el = document.getElementById('donate-box');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 250);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, donationTiers]);

  const handleAmountSelect = (amount, id) => { setSelectedAmount({ amount, id }); setCustomAmount(''); };
  const handleCustomAmount = (e) => { setCustomAmount(e.target.value); setSelectedAmount(null); };
  const currentAmount = selectedAmount?.amount || (customAmount ? parseFloat(customAmount) : 0);

  const handleDonate = async () => {
    if (currentAmount <= 0) return;
    if (currentAmount < 5) { alert('Minimum donation is $5.00'); return; }
    if (donorEmail) { const emailErr = getEmailError(donorEmail); if (emailErr) { alert(emailErr); return; } }
    setIsProcessing(true);
    try {
      // Find tier label for metadata
      const activeTier = selectedAmount ? activeTiers.find(t => t.id === selectedAmount.id) : null;
      const response = await axios.post(`${BACKEND_URL}/api/donations/checkout`, {
        origin_url: window.location.origin,
        package_type: selectedAmount?.id || 'custom',
        custom_amount: selectedAmount ? null : currentAmount,
        donor_name: donorName || null,
        donor_email: donorEmail || null,
        honor_of: honorName || null,
        is_recurring: isRecurring,
        recurring_interval: recurringInterval,
        wall_display: wallDisplay,
        tier_label: activeTier?.label || (dedicationKit ? `Toward ${dedicationKit}` : 'Custom'),
        donation_type: isRecurring ? `recurring_${recurringInterval}` : 'one_time',
        dedication_kit: dedicationKit || null,
        fund_id: selectedFundId || null,
        campaign_slug: campaignSlug || null,
      });
      if (response.data.url) window.location.href = response.data.url;
    } catch (error) {
      logger.error('Error creating checkout:', error);
      alert('Error processing donation. Please try again.');
    } finally { setIsProcessing(false); }
  };

  const progressPercentage = fundraisingGoal
    ? Math.min((fundraisingGoal.current_amount / fundraisingGoal.goal_amount) * 100, 100) : 0;

  const sectionTitle = donationTiers?.section_title || 'Support a Family Today';
  const sectionSubtitle = donationTiers?.section_subtitle || 'Your donation provides food and hygiene essentials to families in need.';
  const anchorText = donationTiers?.anchor_text || '$100 = 1 Family Supported';
  const microcopy = donationTiers?.microcopy || 'Every dollar goes directly toward food and essential supplies for families.';

  // HARD BLOCK screen — donor followed a QR / share link for an
  // archived, inactive, deleted, or nonexistent campaign. Do NOT
  // silently convert into a general donation. Force explicit choice.
  if (campaignBlocked) {
    return (
      <div>
        <div className="bg-[#BA1826] py-6 text-center">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white">Donate</h1>
        </div>
        <div className="py-16 px-6" data-testid="campaign-unavailable-block">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-16 h-16 mx-auto mb-5 bg-[#FEF2F2] border border-[#FECACA] rounded-full flex items-center justify-center">
              <Heart className="w-8 h-8 text-[#BA1826]/70" />
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-3">
              This campaign is no longer accepting donations
            </h2>
            <p className="text-[#4B5563] mb-8 leading-relaxed">
              The campaign you followed is closed, archived, or no longer available.
              Your generosity is still needed — please choose how you'd like to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="/campaigns"
                className="btn-primary inline-flex items-center justify-center gap-1.5"
                data-testid="campaign-blocked-view-active-btn"
              >
                View active campaigns
                <ArrowRight className="w-4 h-4" />
              </a>
              <button
                type="button"
                onClick={() => {
                  // Explicit opt-in to general donation — clear ALL
                  // campaign context so the checkout can't accidentally
                  // reattach the invalid slug.
                  setCampaignBlocked(null);
                  setCampaignSlug('');
                  setCampaign(null);
                  // Strip the ?campaign= param from the URL so a refresh
                  // doesn't re-trigger the block screen.
                  window.history.replaceState({}, '', '/donate');
                }}
                className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 border border-[#0A0A0A] text-[#0A0A0A] rounded font-medium hover:bg-[#0A0A0A] hover:text-white transition-colors"
                data-testid="campaign-blocked-general-donation-btn"
              >
                Make a general donation
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="bg-[#BA1826] py-6 text-center">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white">Donate</h1>
      </div>

      {/* Campaign attribution banner */}
      {campaign && (
        <section className="bg-[#BA1826] text-white border-b border-[#0A0A0A]" data-testid="campaign-attribution-banner">
          <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/15 border border-white/30 flex items-center justify-center flex-shrink-0">
              <Heart className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/70 font-bold">You&apos;re Supporting</p>
              <p className="text-sm sm:text-base font-semibold text-white" data-testid="campaign-banner-name">
                {campaign.name}
                {campaign.country_name && <span className="text-white/70 font-normal"> · {campaign.country_name}</span>}
              </p>
            </div>
            <button
              type="button"
              onClick={() => { setCampaignSlug(''); setCampaign(null); }}
              className="text-[10px] uppercase tracking-wider text-white/70 hover:text-white underline"
              data-testid="campaign-banner-clear"
            >
              Clear
            </button>
          </div>
        </section>
      )}

      {/* Kit Dedication Banner */}
      {dedicationKit && (
        <section className="bg-[#0A0A0A] text-white border-b border-[#BA1826]" data-testid="kit-dedication-banner">
          <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#BA1826]/15 border border-[#BA1826] flex items-center justify-center flex-shrink-0">
              <Gift className="w-5 h-5 text-[#BA1826]" />
            </div>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA1826] font-bold">You're Sponsoring</p>
              <p className="text-sm sm:text-base font-semibold text-white">
                Your donation is dedicated toward the <span className="text-[#BA1826]">{dedicationKit}</span>.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setDedicationKit('')}
              className="text-[10px] uppercase tracking-wider text-white/60 hover:text-white underline"
              data-testid="kit-dedication-clear"
            >
              Clear
            </button>
          </div>
        </section>
      )}

      {/* 501(c)(3) IRS Recognition Notice */}
      <section className="bg-[#FFFBEB] border-b border-[#FDE68A] py-3 px-6" data-testid="donate-501c3-notice">
        <p className="text-xs text-[#78350F] text-center leading-relaxed max-w-2xl mx-auto">
          Novena Humanitarian Foundation, Inc. is an IRS-recognized 501(c)(3) public charity. Contributions are tax-deductible to the extent permitted by law.
        </p>
      </section>

      {/* Hero — Impact Anchor */}
      <section className="relative py-14 md:py-20" data-testid="donate-hero">
        <div className="absolute inset-0 z-0" style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #1a1a2e 50%, #0A0A0A 100%)' }}></div>
        <div className="relative z-10 text-center text-white px-6">
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold mb-3" data-testid="donate-headline">
            {sectionTitle}
          </h2>
          <p className="text-base md:text-lg text-white/90 max-w-xl mx-auto mb-6">
            {sectionSubtitle}
          </p>
          {/* Impact Anchor — prominent */}
          <div className="inline-block bg-white/10 border border-white/20 rounded-xl px-8 py-4 mb-6" data-testid="impact-anchor">
            <p className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-white">{anchorText}</p>
          </div>
          <div>
            <a href="#donate-box" className="btn-primary inline-flex" data-testid="donate-hero-btn">
              Donate Now <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Fundraising Progress */}
      {fundraisingGoal && (
        <section className="py-8 bg-white border-b border-[#E5E7EB]" data-testid="fundraising-progress">
          <div className="px-6 max-w-xl mx-auto">
            <h3 className="font-heading text-lg font-semibold text-center text-[#0A0A0A] mb-2">{fundraisingGoal.title}</h3>
            <p className="text-sm text-[#4B5563] text-center mb-4">{fundraisingGoal.description}</p>
            <div className="relative h-6 bg-[#F1F5F9] rounded-full overflow-hidden mb-3">
              <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#BA1826] to-[#E53935] rounded-full transition-all duration-500"
                style={{ width: `${progressPercentage}%` }}></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-semibold text-[#0A0A0A]">{progressPercentage.toFixed(0)}%</span>
              </div>
            </div>
            <div className="flex justify-between text-sm">
              <span className="font-semibold text-[#BA1826]">${fundraisingGoal.current_amount.toLocaleString()} raised</span>
              <span className="text-[#4B5563]">Goal: ${fundraisingGoal.goal_amount.toLocaleString()}</span>
            </div>
          </div>
        </section>
      )}

      {/* Donation Tier Cards */}
      <section className="py-12 bg-[#F8F9FA]" data-testid="donation-tiers-section">
        <div className="px-6 max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-xs uppercase tracking-[0.15em] text-[#BA1826] font-semibold mb-2">Choose Your Impact</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-2">Make Your Impact</h2>
          </div>

          {/* Tier Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-6" data-testid="tier-grid" data-no-translate="true">
            {activeTiers.map((tier) => {
              const isSelected = selectedAmount?.id === tier.id;
              return (
                <button key={tier.id}
                  onClick={() => handleAmountSelect(tier.amount, tier.id)}
                  className={`relative p-4 md:p-6 text-center cursor-pointer border-2 rounded-xl transition-all ${
                    isSelected ? 'border-[#BA1826] bg-[#FEF2F2] shadow-lg scale-[1.02]' :
                    tier.popular ? 'border-[#BA1826] bg-white hover:bg-[#FEF2F2] shadow-md' :
                    'border-[#E5E7EB] bg-white hover:border-[#BA1826]/50 hover:shadow-sm'
                  }`}
                  data-testid={`tier-card-${tier.amount}`}
                >
                  {tier.popular && (
                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[9px] font-bold text-white bg-[#BA1826] px-2.5 py-0.5 rounded-full whitespace-nowrap">
                      Most Popular
                    </span>
                  )}
                  <div className="font-heading text-2xl md:text-3xl font-bold text-[#BA1826] mb-1">${tier.amount}</div>
                  <p className="text-[10px] md:text-xs text-[#4B5563] leading-tight">{tier.label}</p>
                </button>
              );
            })}
          </div>

          {/* Custom Amount */}
          <div className="flex border-2 border-[#E5E7EB] rounded-xl overflow-hidden bg-white max-w-sm mx-auto mb-4 focus-within:border-[#BA1826]" data-no-translate="true">
            <div className="bg-[#F8F9FA] px-4 py-3 flex items-center border-r border-[#E5E7EB]">
              <DollarSign className="w-5 h-5 text-[#BA1826]" />
            </div>
            <input type="number" placeholder="Custom amount" min="5" value={customAmount} onChange={handleCustomAmount}
              className="flex-1 px-4 py-3 text-sm focus:outline-none" data-testid="custom-amount-input" />
          </div>

          {/* Microcopy */}
          <p className="text-xs text-[#4B5563] text-center italic">{microcopy}</p>
        </div>
      </section>

      {/* Light a Candle */}
      <section className="py-10 bg-white border-y border-[#E5E7EB]" data-testid="candle-section">
        <div className="px-6 max-w-md mx-auto text-center">
          <div className="relative w-14 h-14 mx-auto mb-4">
            <div className="absolute inset-0 bg-gradient-to-t from-[#FEF3C7] to-[#FCD34D] rounded-lg"></div>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2">
              <Flame className="w-6 h-6 text-orange-500 animate-pulse" />
            </div>
          </div>
          <h3 className="font-heading text-xl font-semibold text-[#0A0A0A] mb-2">Light a Candle</h3>
          <p className="text-sm text-[#4B5563] mb-4">Dedicate your donation in honor or memory of someone special.</p>
          <input type="text" placeholder="In honor of... (optional)" value={honorName} onChange={(e) => setHonorName(e.target.value)}
            className="w-full px-4 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:ring-2 focus:ring-[#BA1826]/10"
            data-testid="honor-input" />
        </div>
      </section>

      {/* Donation Form Section */}
      <section id="donate-box" className="py-12 bg-[#F8F9FA]" data-testid="donate-box-section">
        <div className="px-6">
          <div className="max-w-lg mx-auto">
            {/* Transparency Block */}
            <div className="bg-white border border-[#E5E7EB] rounded-sm p-4 mb-6" data-testid="donate-transparency-block">
              <p className="text-sm text-[#4B5563] leading-relaxed">
                Your support helps provide food, hygiene supplies, and essential resources to individuals and families in need. Novena Humanitarian Foundation, Inc. is an IRS-recognized 501(c)(3) public charity — contributions are tax-deductible to the extent permitted by law.
              </p>
            </div>

            <div className="bg-white border border-[#E5E7EB] p-6 rounded-xl shadow-sm">
              <h3 className="font-heading text-xl font-semibold text-[#0A0A0A] text-center mb-6">Complete Your Donation</h3>

              {/* Monthly Toggle */}
              <div className="mb-6 p-4 bg-[#F8F9FA] rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 text-[#BA1826]" />
                    <span className="text-sm font-medium text-[#0A0A0A]">Make this a monthly donation</span>
                  </div>
                  <button onClick={() => setIsRecurring(!isRecurring)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${isRecurring ? 'bg-[#BA1826]' : 'bg-[#E5E7EB]'}`}
                    data-testid="recurring-toggle">
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${isRecurring ? 'translate-x-7' : 'translate-x-1'}`}></div>
                  </button>
                </div>
                {isRecurring && (
                  <div className="flex gap-2">
                    <button onClick={() => setRecurringInterval('month')}
                      className={`flex-1 py-2 text-sm rounded transition-colors ${recurringInterval === 'month' ? 'bg-[#BA1826] text-white' : 'bg-white border border-[#E5E7EB] text-[#4B5563]'}`}>Monthly</button>
                    <button onClick={() => setRecurringInterval('year')}
                      className={`flex-1 py-2 text-sm rounded transition-colors ${recurringInterval === 'year' ? 'bg-[#BA1826] text-white' : 'bg-white border border-[#E5E7EB] text-[#4B5563]'}`}>Yearly</button>
                  </div>
                )}
              </div>

              {/* Donor Info */}
              <div className="space-y-3 mb-6">
                <input type="text" placeholder="Your name (optional)" value={donorName} onChange={(e) => setDonorName(e.target.value)}
                  className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm focus:border-[#BA1826] focus:outline-none" data-testid="donor-name-input" />
                <input type="email" placeholder="Your email (optional)" value={donorEmail} onChange={(e) => setDonorEmail(e.target.value)}
                  className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none ${donorEmail && !isValidEmail(donorEmail) ? 'border-red-400 focus:border-red-500' : 'border-[#E5E7EB] focus:border-[#BA1826]'}`}
                  data-testid="donor-email-input" />
                {donorEmail && !isValidEmail(donorEmail) && <p className="text-[9px] text-red-500 -mt-1">{getEmailError(donorEmail)}</p>}
              </div>

              {/* Phase I.4 — Optional fund designation */}
              {fundDesignation.enabled && fundDesignation.options?.length > 0 && (
                <div className="mb-6 p-4 bg-[#FFF8F8] border border-[#BA1826]/30 rounded-lg" data-testid="donate-fund-designation">
                  <p className="text-sm font-semibold text-[#0A0A0A] mb-1 inline-flex items-center gap-1.5">
                    <Heart className="w-4 h-4 text-[#BA1826]" /> Designate your gift
                  </p>
                  <p className="text-xs text-[#4B5563] mb-2">{fundDesignation.intro_copy}</p>
                  <select value={selectedFundId} onChange={(e) => setSelectedFundId(e.target.value)}
                    className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm bg-white focus:border-[#BA1826] focus:outline-none"
                    data-testid="donate-fund-select">
                    <option value="">— General Support (no specific designation) —</option>
                    {fundDesignation.options.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name}{f.fund_type !== 'unrestricted' ? ` · restricted` : ''}
                      </option>
                    ))}
                  </select>
                  {selectedFundId && fundDesignation.options.find((f) => f.id === selectedFundId)?.description && (
                    <p className="mt-2 text-[11px] italic text-[#4B5563]" data-testid="donate-fund-description">
                      {fundDesignation.options.find((f) => f.id === selectedFundId).description}
                    </p>
                  )}
                </div>
              )}

              {/* Donor Wall Preference */}
              <div className="mb-6 p-4 bg-[#F8F9FA] rounded-lg">
                <p className="text-sm font-medium text-[#0A0A0A] mb-3">Display preference for Donor Wall</p>
                <div className="space-y-2">
                  {[
                    { value: 'full', label: 'Show my name and donation amount' },
                    { value: 'name_only', label: 'Show my name only' },
                    { value: 'anonymous', label: 'Remain anonymous' },
                  ].map((option) => (
                    <label key={option.value} className="flex items-center gap-3 cursor-pointer" onClick={() => setWallDisplay(option.value)} data-testid={`wall-display-${option.value}`}>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${wallDisplay === option.value ? 'border-[#BA1826]' : 'border-[#E5E7EB]'}`}>
                        {wallDisplay === option.value && <div className="w-3 h-3 rounded-full bg-[#BA1826]"></div>}
                      </div>
                      <span className="text-sm text-[#4B5563]">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Donation Summary */}
              {currentAmount > 0 && (
                <div className="mb-6 p-4 border border-[#E5E7EB] rounded-lg bg-[#FEFCE8]">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-[#4B5563]">Donation amount</span>
                    <span className="font-semibold text-[#0A0A0A]">${currentAmount.toLocaleString()}</span>
                  </div>
                  {isRecurring && (
                    <div className="flex justify-between text-sm">
                      <span className="text-[#4B5563]">Frequency</span>
                      <span className="font-semibold text-[#0A0A0A]">{recurringInterval === 'month' ? 'Monthly' : 'Yearly'}</span>
                    </div>
                  )}
                </div>
              )}

              <TransparencyNotice />
              <TransparencyCheckbox checked={transparencyAcknowledged} onChange={(e) => setTransparencyAcknowledged(e.target.checked)} />

              {/* CTA Button — large and prominent. data-no-translate keeps the
                  Stripe-bound button label in English/numeric form. */}
              <button onClick={handleDonate}
                disabled={isProcessing || currentAmount <= 0 || !transparencyAcknowledged}
                className="btn-primary w-full py-4 text-base font-bold disabled:opacity-50 mt-4 flex items-center justify-center gap-2"
                data-testid="main-donate-btn"
                data-no-translate="true">
                {isProcessing ? 'Processing...' : `Donate $${currentAmount ? currentAmount.toLocaleString() : '...'}`}
                {isRecurring && currentAmount > 0 && ` / ${recurringInterval === 'month' ? 'month' : 'year'}`}
              </button>

              {/* Trust Layer */}
              <div className="flex items-center justify-center gap-4 mt-4 text-[10px] text-[#4B5563]">
                <span className="flex items-center gap-1"><Shield className="w-3 h-3 text-green-600" /> Secure payment</span>
                <span className="flex items-center gap-1"><Heart className="w-3 h-3 text-[#BA1826]" /> Direct impact</span>
              </div>

              <div className="mt-4">
                <MerchTransparencyNotice variant="donation" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Donor Wall */}
      <DonorWallSection donorWall={donorWall} />

      {/* Fund Allocation */}
      <FundAllocationChart allocation={allocation} />

      {/* FAQ */}
      <DonateFAQ />

      {/* Footer */}
      <section className="py-8 bg-white border-t border-[#E5E7EB]">
        <div className="text-center">
          <p className="font-heading text-lg italic text-[#4B5563]">
            Transparency without exploitation. Support without shame.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Donate;
